import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Tabs, Spin, Empty, Typography, Tag, Button, message } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import AgentConfigService from '../../services/AgentConfigService';

const { Text } = Typography;

const C = {
  navy:       '#162349',
  green:      '#6B8F3D',
  blue:       '#1E40AF',
  blueBg:     '#EFF6FF',
  blueBorder: '#BFDBFE',
  purple:     '#6B21A8',
  purpleBg:   '#F5F3FF',
  purpleBorder:'#DDD6FE',
  border:     '#E5E7EB',
  textMuted:  '#6B7280',
  bg:         '#F9FAFB',
};

// ── Parse encounter_id into readable format ─────────────────────────────────
// "encounter_001_p0007_p0028" → "Encounter 1 (p 7 – 28)"
function formatEncounterId(encId, encounterDate) {
  if (!encId) return '—';
  const match = encId.match(/encounter_(\d+)_p(\d+)_p(\d+)/);
  if (!match) return encId;
  const num   = parseInt(match[1], 10);
  const start = parseInt(match[2], 10);
  const end   = parseInt(match[3], 10);
  let label = `Encounter ${num} (p ${start} – ${end})`;
  if (encounterDate) label += `  ·  ${encounterDate}`;
  return label;
}


// ── Single code card ────────────────────────────────────────────────────────
const CodeCard = ({ decision }) => {
  const evidence = decision.evidence || [];
  const support  = decision.support  || [];
  const encLabel = formatEncounterId(decision.encounter_id, decision.encounter_date);

  return (
    <div style={{
      background: 'white',
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      padding: '14px 18px',
      marginBottom: 10,
    }}>
      {/* Code header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Tag color="blue" style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>
          {decision.icd10_code}
        </Tag>
        <Text style={{ fontSize: 16, fontWeight: 600, color: '#1F2937' }}>
          {decision.code_description || '—'}
        </Text>
        <Text style={{ fontSize: 14, color: C.textMuted, marginLeft: 'auto' }}>
          {decision.source === 'manually_added' ? 'Coder Added' : 'AI Proposed'}
        </Text>
      </div>

      {/* Encounter info */}
      <div style={{ fontSize: 15, color: C.textMuted, marginBottom: 10 }}>
        {encLabel}
      </div>

      {/* Evidence */}
      {evidence.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 14, fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Evidence
          </Text>
          {evidence.map((e, i) => (
            <div key={i} style={{
              fontSize: 15, lineHeight: 1.6,
              background: C.blueBg, border: `1px solid ${C.blueBorder}`,
              borderRadius: 4, padding: '8px 12px', marginTop: 5,
              color: '#374151',
            }}>
              "{e.text}"
              {e.page && (
                <span style={{ fontSize: 13, color: C.textMuted, marginLeft: 8 }}>
                  p.{e.page}{e.section ? ` · ${e.section}` : ''}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Support */}
      {support.length > 0 && (
        <div>
          <Text style={{ fontSize: 14, fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Support
          </Text>
          {support.map((s, i) => (
            <div key={i} style={{
              fontSize: 15, lineHeight: 1.6,
              background: '#F0FDF4', border: '1px solid #BBF7D0',
              borderRadius: 4, padding: '8px 12px', marginTop: 5,
              color: '#374151',
            }}>
              "{s.text}"
              {s.page && (
                <span style={{ fontSize: 13, color: C.textMuted, marginLeft: 8 }}>
                  p.{s.page}{s.type ? ` · ${s.type}` : ''}{s.section ? ` · ${s.section}` : ''}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ── HCC group ───────────────────────────────────────────────────────────────
const HccGroup = ({ categoryCode, codes, colorScheme }) => {
  const { bg, border, tagColor } = colorScheme;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px',
        background: bg, border: `1px solid ${border}`,
        borderRadius: 6, marginBottom: 10,
      }}>
        <Tag color={tagColor} style={{ fontWeight: 700, fontSize: 18, margin: 0 }}>
          {categoryCode}
        </Tag>
        <Text style={{ fontSize: 16, fontWeight: 600, color: '#1F2937' }}>
          {codes.length} {codes.length === 1 ? 'code' : 'codes'}
        </Text>
      </div>

      <div style={{ paddingLeft: 14 }}>
        {codes.map((d, i) => (
          <CodeCard key={`${d.icd10_code}-${d.encounter_id}-${i}`} decision={d} />
        ))}
      </div>
    </div>
  );
};


// ── Group decisions by HCC category ─────────────────────────────────────────
function groupByHcc(decisions, field) {
  const groups = {};
  const unmapped = [];

  decisions.forEach(d => {
    const cat = d[field];
    if (cat) {
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(d);
    } else {
      unmapped.push(d);
    }
  });

  const sorted = Object.entries(groups)
    .sort(([a], [b]) => {
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });

  return { sorted, unmapped };
}


// ── Tab content ─────────────────────────────────────────────────────────────
const HccTabContent = ({ decisions, field, colorScheme }) => {
  const { sorted, unmapped } = useMemo(
    () => groupByHcc(decisions, field),
    [decisions, field]
  );

  if (decisions.length === 0) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center' }}>
        <Empty description="No accepted codes" />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Summary */}
      <div style={{
        display: 'flex', gap: 20, marginBottom: 22,
        padding: '14px 18px', background: 'white',
        border: `1px solid ${C.border}`, borderRadius: 6,
      }}>
        <div>
          <Text style={{ fontSize: 28, fontWeight: 700, color: C.navy }}>{decisions.length}</Text>
          <Text style={{ fontSize: 15, color: C.textMuted, marginLeft: 6 }}>codes accepted</Text>
        </div>
        <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 20 }}>
          <Text style={{ fontSize: 28, fontWeight: 700, color: C.navy }}>{sorted.length}</Text>
          <Text style={{ fontSize: 15, color: C.textMuted, marginLeft: 6 }}>HCC categories</Text>
        </div>
        {unmapped.length > 0 && (
          <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 20 }}>
            <Text style={{ fontSize: 28, fontWeight: 700, color: '#D97706' }}>{unmapped.length}</Text>
            <Text style={{ fontSize: 15, color: C.textMuted, marginLeft: 6 }}>unmapped</Text>
          </div>
        )}
      </div>

      {sorted.map(([cat, codes]) => (
        <HccGroup key={cat} categoryCode={cat} codes={codes} colorScheme={colorScheme} />
      ))}

      {unmapped.length > 0 && (
        <HccGroup
          categoryCode="Unmapped"
          codes={unmapped}
          colorScheme={{ bg: '#FEF3C7', border: '#FDE68A', tagColor: 'orange' }}
        />
      )}
    </div>
  );
};


// ── Main modal ──────────────────────────────────────────────────────────────
const FinalizeReviewModal = ({
  visible,
  chartId,
  sessionId,
  onClose,
  onFinalized,
}) => {
  const [loading, setLoading]       = useState(false);
  const [decisions, setDecisions]   = useState([]);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    if (!visible || !chartId) return;

    const fetchDecisions = async () => {
      setLoading(true);
      try {
        const base = await AgentConfigService.getWorkQueueServiceUrl();
        const resp = await fetch(`${base}/hcc/charts/${chartId}/decisions`, {
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const accepted = (data.decisions || []).filter(d => d.decision === 'accepted');
        setDecisions(accepted);
      } catch (err) {
        console.error('Failed to fetch decisions:', err);
        message.error('Failed to load accepted codes');
      } finally {
        setLoading(false);
      }
    };

    fetchDecisions();
  }, [visible, chartId, sessionId]);

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const base = await AgentConfigService.getWorkQueueServiceUrl();
      const resp = await fetch(`${base}/work-items/${chartId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionId}` },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      message.success('Chart finalized and marked as complete');
      onFinalized();
    } catch (err) {
      console.error('Finalize error:', err);
      message.error('Failed to finalize chart');
    } finally {
      setFinalizing(false);
    }
  };

  const v28Scheme   = { bg: C.blueBg, border: C.blueBorder, tagColor: 'blue' };
  const rxhccScheme = { bg: C.purpleBg, border: C.purpleBorder, tagColor: 'purple' };

  const tabItems = [
    {
      key: 'v28',
      label: <span style={{ fontSize: 16, fontWeight: 600 }}>HCC V28</span>,
      children: <HccTabContent decisions={decisions} field="hcc_v28" colorScheme={v28Scheme} />,
    },
    {
      key: 'rxhcc',
      label: <span style={{ fontSize: 16, fontWeight: 600 }}>RxHCC</span>,
      children: <HccTabContent decisions={decisions} field="rxhcc" colorScheme={rxhccScheme} />,
    },
  ];

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      width="65vw"
      style={{ top: 30 }}
      styles={{ body: { maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      footer={null}
      destroyOnClose
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>
            Finalize Chart Review
          </div>
          <Text style={{ fontSize: 15, color: C.textMuted }}>
            Review all accepted codes grouped by HCC category before finalizing
          </Text>
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, color: C.textMuted }}>
          {chartId}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '80px 0', textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 16, color: C.textMuted }}>
            Loading accepted codes...
          </div>
        </div>
      ) : (
        <>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <Tabs items={tabItems} size="large" style={{ marginBottom: 0 }} />
          </div>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 12,
            paddingTop: 16, borderTop: `1px solid ${C.border}`, marginTop: 0,
            flexShrink: 0,
          }}>
            <Button size="large" onClick={onClose} style={{ fontSize: 15, height: 44 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={handleFinalize}
              loading={finalizing}
              disabled={decisions.length === 0}
              style={{
                background: C.green, borderColor: C.green,
                fontWeight: 700, fontSize: 16,
                height: 44, paddingLeft: 24, paddingRight: 24,
              }}
            >
              Finalize & Complete ({decisions.length} codes)
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default FinalizeReviewModal;